//Helpers
import { average, sortOn } from 'src/helpers/utilities';
import { dateFormat } from 'src/helpers/date_helper';
import type { ChartControlsDataInterface } from '../../ui/chart_control_helpers';
import { deepCloneObj } from '../../core/chart_helper';
//Types
import type {
	GetStatsResponseData,
	GetStatsResponseHistoryData,
} from 'src/types/requests/charts/get_stats_period';

/**
 * Adds the last hour's measurement statistics to the current statistics
 * if the current time period includes the last hour.
 *
 * @param {GetStatsResponseData} resData - The original response data that will be updated.
 * @param {GetStatsResponseData} lastHourRes - The response data containing the last hour's measurements.
 * @param {ChartControlsDataInterface} chartControlsData - Configuration object controlling the chart's display settings.
 * @param {boolean} [isGBLE=false] - Indicates whether the device is of type GBLE.
 * GBLE devices do not have timezone settings and always use UTC time.
 * @returns {GetStatsResponseData} The updated response data with the last hour's measurements integrated.
 */

export const addLastHourMeasurement = (
	resData: GetStatsResponseData,
	lastHourRes: GetStatsResponseData,
	chartControlsData: ChartControlsDataInterface,
	isGBLE = false,
): GetStatsResponseData => {
	if (!lastHourRes?.history?.length) return resData;
	resData = deepCloneObj(resData);
	const lastHourResHistory: GetStatsResponseHistoryData[] = lastHourRes.history,
		firstItem = lastHourResHistory[0],
		date = new Date(firstItem.datetime);
	date.setMinutes(0);
	if (!/^(24_hours|day)$/.test(chartControlsData.rangeOption.period)) {
		date.setHours(0);
	}
	if (
		/^(6_months|12_months|year)$/.test(chartControlsData.rangeOption.period) ||
		(chartControlsData.rangeOption.period == 'custom' && resData?.interval == 'month')
	) {
		date.setDate(1);
	}
	resData.history = addLastHourMeasurementDateset(
		resData?.history || [],
		lastHourResHistory,
		date,
		isGBLE,
	);
	return resData;
};

export const addLastHourMeasurementDateset = (
	resHistory: GetStatsResponseHistoryData[],
	lastHourResHistory: GetStatsResponseHistoryData[],
	date: Date,
	isGBLE = false,
): GetStatsResponseHistoryData[] => {
	const measurementFormat = 'Y-m-dTH:i:s';
	const datetime: string = dateFormat(date, measurementFormat, isGBLE);
	const resHistoryCopy: GetStatsResponseHistoryData[] = resHistory.filter(
		(i) => !i?.missing && dateFormat(i.datetime, measurementFormat, isGBLE) != datetime,
	);
	const avg: number[] = [];
	const propAvg: { prop?: string; values?: number[] } = {};
	const item: GetStatsResponseHistoryData = resHistory.find(
		(i) => dateFormat(i.datetime, measurementFormat, isGBLE) == datetime,
	) || { datetime, missing: true };
	for (let i = 0; i < lastHourResHistory.length; i++) {
		const historyItem: GetStatsResponseHistoryData = lastHourResHistory[i];
		if (historyItem?.missing) continue;
		for (const prop in historyItem) {
			if (prop == 'datetime') continue;
			item.missing = false;
			if (/^(min|max)$/.test(prop)) {
				item[prop] = Math[prop](item[prop] ?? historyItem[prop], historyItem[prop]);
			} else if (prop == 'avg') {
				avg.push(historyItem.avg || 0);
			} else if (/^(value|precipitation)$/.test(prop)) {
				item[prop] = (item[prop] || 0) + (historyItem[prop] || 0);
			} else {
				if (!propAvg[prop]) propAvg[prop] = [];
				propAvg[prop].push(historyItem[prop] || 0);
			}
		}
	}
	if (avg.length) {
		if (item.avg != null) avg.push(item.avg || 0);
		item.avg = average(avg);
	} else {
		for (const prop in propAvg) {
			if (item[prop] != null) propAvg[prop].push(item[prop] || 0);
			item[prop] = average(propAvg[prop]);
		}
	}
	resHistoryCopy.push(item);
	return sortOn(resHistoryCopy, 'datetime');
};
